import { useEffect, useRef, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";

// === Configuration ===
const BOOKING_URL = "https://calendly.com/kristenhinman/leverage";
const TRY_PRODUCTS_URL_START =
    "https://leveragequiz.com/try-products?utm_source=practice&utm_content=start";
const TRY_PRODUCTS_URL_FINAL =
    "https://leveragequiz.com/try-products?utm_source=practice&utm_content=final";
const IDS_URL = "https://monatglobal.com/income-disclosure-statement/";

const MAIN_VIDEO_ID = "YnSgb9toClU"; // kristen's 12-min overview
const PROOF_SHORT_VIDEO_ID = "YMzNCzLdHBo"; // dr. michelle short

// portraits
const MICHELLE_PORTRAIT =
    "https://customer-assets.emergentagent.com/job_income-breakthrough-1/artifacts/vu0jsvdw_IMG_0729.png";
const PAIGE_PORTRAIT =
    "https://customer-assets-4nw71qhi.emergentagent.net/job_income-breakthrough-1/artifacts/3d57d7f99d14d716_paige-lynch.jpg";
const JEN_PORTRAIT =
    "https://customer-assets-4nw71qhi.emergentagent.net/job_income-breakthrough-1/artifacts/33f8d477bb6c3e25_jen-clemons.jpg";
const DEVON_PORTRAIT =
    "https://customer-assets-4nw71qhi.emergentagent.net/job_income-breakthrough-1/artifacts/42dd98ae1e14c5f4_devon-donkin.jpg";
const KRISTEN_PORTRAIT =
    "https://customer-assets.emergentagent.com/job_income-breakthrough-1/artifacts/j88louvc_Brown%20and%20Light%20Brown%2C%20Circle%20Framed%20Instagram%20Profile%20Picture%20%281%29.png";
const KRISTEN_VIDEO_POSTER = KRISTEN_PORTRAIT;

const THINNING_BEFORE =
    "https://customer-assets-4nw71qhi.emergentagent.net/job_income-breakthrough-1/artifacts/2b3530b31b5aac5d_thinning-hair-before.jpg";
const THINNING_AFTER =
    "https://customer-assets-4nw71qhi.emergentagent.net/job_income-breakthrough-1/artifacts/7cf9d56d9d91a95e_thinning-hair-after.jpg";
const SILVER_HAIR_PAIR =
    "https://customer-assets-4nw71qhi.emergentagent.net/job_income-breakthrough-1/artifacts/893348a36d5c9834_IMG_9775%206.jpg";
const MAN_HAIR_PAIR =
    "https://customer-assets-4nw71qhi.emergentagent.net/job_income-breakthrough-1/artifacts/244d32901618dd39_IMG_7339%202.JPG";

const LimePlayIcon = () => (
    <svg
        width="28"
        height="28"
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
    >
        <path d="M8 5v14l11-7z" />
    </svg>
);

const VideoEmbed = ({ videoId, label, testId, poster, vertical }) => {
    const [playing, setPlaying] = useState(false);
    return (
        <div className="lp-video-block">
            {label && (
                <div className="lp-video-label" data-testid={`${testId}-label`}>
                    {label}
                </div>
            )}
            <div
                className={`lp-video-wrapper${vertical ? " lp-video-wrapper--vertical" : ""}`}
                data-testid={`${testId}-wrapper`}
            >
                {playing ? (
                    <iframe
                        src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1&playsinline=1`}
                        title={label || "video"}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        data-testid={`${testId}-iframe`}
                    />
                ) : (
                    <button
                        type="button"
                        className="lp-play-overlay"
                        onClick={() => setPlaying(true)}
                        aria-label={`play ${label || "video"}`}
                        data-testid={`${testId}-play-button`}
                    >
                        <div
                            className="lp-video-poster"
                            style={
                                poster
                                    ? { backgroundImage: `url(${poster})` }
                                    : undefined
                            }
                        />
                        <span className="lp-play-button-lime">
                            <LimePlayIcon />
                        </span>
                    </button>
                )}
            </div>
        </div>
    );
};

const Eyebrow = ({ children, testId }) => (
    <div className="lp-eyebrow" data-testid={testId}>
        <span className="lp-eyebrow-rule" aria-hidden="true" />
        <span className="lp-eyebrow-text">{children}</span>
    </div>
);

const FaqItem = ({ q, a, testId, defaultOpen }) => {
    const [open, setOpen] = useState(!!defaultOpen);
    return (
        <div
            className={`lp-faq-item${open ? " is-open" : ""}`}
            data-testid={testId}
        >
            <button
                type="button"
                className="lp-faq-q"
                onClick={() => setOpen((o) => !o)}
                aria-expanded={open}
                data-testid={`${testId}-trigger`}
            >
                <span>{q}</span>
                <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                >
                    <path d="M6 9l6 6 6-6" />
                </svg>
            </button>
            {open && (
                <div className="lp-faq-a" data-testid={`${testId}-answer`}>
                    {a}
                </div>
            )}
        </div>
    );
};

const TeamCard = ({ photo, name, creds, handle, instagram, quote, testId }) => (
    <article className="lp-team-tile" data-testid={testId}>
        <div
            className="lp-team-photo"
            role="img"
            aria-label={name}
            style={{ backgroundImage: `url(${photo})` }}
        />
        <div className="lp-team-body">
            {quote && <p className="lp-team-quote">{quote}</p>}
            <p className="lp-team-id">
                <span className="lp-team-name">{name}</span>
                <span className="lp-team-creds">{creds}</span>
                <a
                    className="lp-team-handle"
                    href={instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`${testId}-handle`}
                >
                    {handle} →
                </a>
            </p>
        </div>
    </article>
);

// Income table data (exact figures from brief)
const INCOME_ROWS = [
    ["market partner", "91.69%", "$7"],
    ["associate market partner", "3.74%", "$74"],
    ["managing market partner", "2.26%", "$147"],
    ["associate market builder", "1.02%", "$328"],
    ["market builder", "0.37%", "$582"],
    ["managing market builder", "0.49%", "$943"],
    ["associate market mentor", "0.21%", "$2,175"],
    ["market mentor", "0.13%", "$5,093"],
    ["managing market mentor", "0.05%", "$12,142"],
    ["associate executive director", "0.02%", "$25,515"],
    ["executive director", "0.01%", "$40,220"],
    ["senior executive director", "0.01%", "$102,318"],
];

const Landing = () => {
    const [stickyVisible, setStickyVisible] = useState(false);
    const heroRef = useRef(null);

    useEffect(() => {
        const onScroll = () => {
            if (!heroRef.current) return;
            const bottom = heroRef.current.getBoundingClientRect().bottom;
            setStickyVisible(bottom < 0);
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const goBook = () => {
        window.open(BOOKING_URL, "_blank", "noopener");
    };
    const goTryProductsStart = () => {
        window.open(TRY_PRODUCTS_URL_START, "_blank", "noopener");
    };

    return (
        <div className="App" data-testid="landing-page">
            <main className="lp-container">
                {/* === 1. HERO === */}
                <section
                    ref={heroRef}
                    className="lp-section lp-hero"
                    data-testid="hero-section"
                >
                    <p className="lp-small-label" data-testid="top-mark">
                        kristen hinman · private practice briefing
                    </p>

                    <Eyebrow testId="eyebrow">
                        for practitioners &amp; doctors
                    </Eyebrow>

                    <h1 className="lp-headline" data-testid="headline">
                        what if your recommendations kept paying you after
                        the appointment ends?
                    </h1>

                    <p className="lp-subhead" data-testid="subheadline">
                        for practitioners who are at capacity but still
                        capped financially.
                    </p>

                    <div className="lp-hero-video" data-testid="hero-video">
                        <VideoEmbed
                            videoId={MAIN_VIDEO_ID}
                            testId="main-video"
                            poster={`https://img.youtube.com/vi/${MAIN_VIDEO_ID}/maxresdefault.jpg`}
                        />
                    </div>

                    <button
                        type="button"
                        className="lp-btn lp-btn--lime"
                        onClick={goBook}
                        data-testid="hero-cta-book-call"
                    >
                        book a 30-minute call
                    </button>
                </section>

                {/* === 2. CREDIBILITY STRIP === */}
                <section
                    className="lp-cred-strip"
                    data-testid="section-credibility"
                >
                    <span>clinical grade</span>
                    <span className="lp-cred-dot" aria-hidden="true">·</span>
                    <span>plant-based</span>
                    <span className="lp-cred-dot" aria-hidden="true">·</span>
                    <span>passes eu standards</span>
                    <span className="lp-cred-dot" aria-hidden="true">·</span>
                    <span>3 million+ customers</span>
                </section>

                {/* === 3. THE PROBLEM === */}
                <section
                    className="lp-section lp-section--problem"
                    data-testid="section-problem"
                >
                    <h2 className="lp-h2" data-testid="problem-headline">
                        right now, you only get paid when you are in the
                        room.
                    </h2>

                    <Eyebrow>why this happens</Eyebrow>
                    <h3 className="lp-h3">you've already done the obvious things.</h3>

                    <ul className="lp-list">
                        <li>you've raised your prices at least once</li>
                        <li>you've optimized your appointment structure</li>
                        <li>you're seeing back-to-back patients</li>
                        <li>you've taken insurance and been underpaid</li>
                        <li>
                            or gone cash pay and felt the pressure to keep
                            your schedule full
                        </li>
                    </ul>

                    <p>
                        and still, at the end of the month, your revenue
                        looks almost identical to last month. there is a
                        limit to how many patients you can physically see in
                        a day and you've already hit it.
                    </p>

                    <p>
                        you're already having the right conversations. you
                        explain root causes. you recommend solutions. your
                        patients follow through. the issue is how you get
                        paid. every time they leave, your revenue resets.
                    </p>
                </section>

                {/* === 4. INSIDE A REAL CLINIC === */}
                <section
                    className="lp-section lp-section--alt"
                    data-testid="section-inside-clinic"
                >
                    <Eyebrow>inside a real clinic</Eyebrow>
                    <h2 className="lp-h2">
                        here's what this looks like inside a real clinic.
                    </h2>
                    <div className="lp-proof-short">
                        <VideoEmbed
                            videoId={PROOF_SHORT_VIDEO_ID}
                            testId="inside-clinic-short"
                            vertical
                        />
                        <p className="lp-attribution">
                            dr. michelle mattingly, ND, LAc
                        </p>
                    </div>
                </section>

                {/* === 5. THE SHIFT === */}
                <section
                    className="lp-section"
                    data-testid="section-solution-shift"
                >
                    <Eyebrow>the shift</Eyebrow>
                    <h2 className="lp-h2">
                        you're already making the recommendation. you're
                        just not getting paid beyond the appointment for it.
                    </h2>

                    <p>
                        we are working with clinical-grade, plant-based
                        haircare, for patients who are already asking you
                        about thinning, shedding after illness or stress,
                        postpartum hair and menopause hair changes.
                    </p>

                    <p>
                        you don't need more appointments. you don't need
                        longer days. you don't need to hire anyone.
                    </p>

                    <ol className="lp-steps" data-testid="shift-steps">
                        <li>
                            <span className="lp-step-num">1</span>
                            <span className="lp-step-text">
                                you recommend it in the appointment, the
                                same way you recommend anything else.
                            </span>
                        </li>
                        <li>
                            <span className="lp-step-num">2</span>
                            <span className="lp-step-text">
                                your patient orders from her phone through
                                your link, and it ships to her door.
                            </span>
                        </li>
                        <li>
                            <span className="lp-step-num">3</span>
                            <span className="lp-step-text">
                                when it works, she reorders through you.
                            </span>
                        </li>
                    </ol>

                    <p className="lp-highlight">
                        monat pays 10-15% more than fullscript, and it does
                        not stop at the first sale.
                    </p>

                    <p>
                        it compounds because people use it up &amp;
                        reorder. once a patient orders through your link,
                        her reorders come through you too.
                    </p>
                </section>

                {/* === 6. BEFORE / AFTER === */}
                <section
                    className="lp-section lp-section--alt"
                    data-testid="section-before-after"
                >
                    <h2 className="lp-h2">
                        same patient. same consultation. getting paid more
                        than once.
                    </h2>

                    <div className="lp-ba-grid">
                        <div
                            className="lp-ba-card"
                            data-testid="ba-before"
                        >
                            <span className="lp-ba-label">before</span>
                            <ul className="lp-list">
                                <li>constant patient load</li>
                                <li>back-to-back appointments</li>
                                <li>your income depends on your calendar</li>
                                <li>no revenue beyond the visit</li>
                                <li>
                                    if you're not working, it's not coming in
                                </li>
                            </ul>
                        </div>

                        <div
                            className="lp-ba-card lp-ba-card--ink"
                            data-testid="ba-after"
                        >
                            <span className="lp-ba-label lp-ba-label--on-ink">
                                after
                            </span>
                            <ul className="lp-list lp-list--on-ink">
                                <li>
                                    the recommendation doesn't stop at the
                                    appointment
                                </li>
                                <li>
                                    patients stay consistent with it and
                                    see results
                                </li>
                                <li>
                                    they reorder it on their own, right from
                                    their phone, through you
                                </li>
                                <li>and you get paid more than once</li>
                            </ul>
                        </div>
                    </div>

                    <div className="lp-thinning" data-testid="thinning-pair">
                        <figure>
                            <img
                                src={THINNING_BEFORE}
                                alt="before"
                                loading="lazy"
                            />
                            <figcaption>before</figcaption>
                        </figure>
                        <figure>
                            <img
                                src={THINNING_AFTER}
                                alt="after"
                                loading="lazy"
                            />
                            <figcaption>after</figcaption>
                        </figure>
                        <figure
                            className="lp-thinning-combo"
                            data-testid="silver-hair-pair"
                        >
                            <img
                                src={SILVER_HAIR_PAIR}
                                alt="silver hair before and after"
                                loading="lazy"
                            />
                        </figure>
                        <figure
                            className="lp-thinning-combo"
                            data-testid="man-hair-pair"
                        >
                            <img
                                src={MAN_HAIR_PAIR}
                                alt="before and after"
                                loading="lazy"
                            />
                        </figure>
                    </div>
                    <p className="lp-fineprint">
                        real customer experiences. individual results vary.
                    </p>
                </section>

                {/* === 7. THE EXPERTS ON MY TEAM === */}
                <section
                    className="lp-section"
                    data-testid="section-team"
                >
                    <Eyebrow>the experts on my team</Eyebrow>
                    <h2 className="lp-h2">
                        the women who read the labels first.
                    </h2>
                    <p className="lp-subhead">
                        doctors &amp; practitioners who are market partners
                        on my team. here's why they said yes.
                    </p>

                    <div className="lp-team-grid">
                        <TeamCard
                            testId="team-michelle"
                            photo={MICHELLE_PORTRAIT}
                            quote={
                                "hair loss is one of the first things my patients bring up, and for years i didn't have a topical i trusted. now i do, and recommending it doesn't take another appointment slot."
                            }
                            name="dr. michelle mattingly"
                            creds="ND, LAc · naturopathic doctor of 20 years & licensed acupuncturist"
                            handle="@drmichellemattingly"
                            instagram="https://www.instagram.com/drmichellemattingly/"
                        />
                        <TeamCard
                            testId="team-paige"
                            photo={PAIGE_PORTRAIT}
                            quote={
                                "i read ingredient labels for a living, so i used these on myself first. they made the biggest difference. then my patients saw it too, and only then did i put my name on them."
                            }
                            name="paige s. lynch"
                            creds="NP · triple board certified NP & functional medicine practitioner"
                            handle="@paigeslynchnp"
                            instagram="https://www.instagram.com/paigeslynchnp/"
                        />
                        <TeamCard
                            testId="team-jen"
                            photo={JEN_PORTRAIT}
                            quote={
                                "i'm a mother, a doctor and a teacher. i didn't need another job. this had to serve my patients, make sense in the work i already do, and give me a new way to use what i know."
                            }
                            name="dr. jen clemons"
                            creds="ND · naturopathic doctor · wise owl medicine"
                            handle="@drjenclemons"
                            instagram="https://www.instagram.com/drjenclemons/"
                        />
                        <TeamCard
                            testId="team-devon"
                            photo={DEVON_PORTRAIT}
                            quote={
                                "i'm a nurse and a health coach, so i'm recommending things to people all day. this month my team started growing, and it's been so much fun."
                            }
                            name="devon donkin"
                            creds="RN · holistic health RN, health coach & nicu nurse"
                            handle="@devon__donkin"
                            instagram="https://www.instagram.com/devon__donkin/"
                        />
                    </div>

                    <p className="lp-fineprint">
                        all four are independent monat market partners on my
                        team. individual results vary.
                    </p>

                    <article
                        className="lp-kristen-card"
                        data-testid="team-kristen"
                    >
                        <img
                            src={KRISTEN_PORTRAIT}
                            alt="kristen hinman"
                            className="lp-kristen-photo"
                        />
                        <div>
                            <p className="lp-team-name">kristen hinman</p>
                            <p className="lp-team-bio">
                                15+ years working behind the scenes with
                                doctors and clinical practitioners, focused
                                on how revenue actually functions inside
                                private practice.
                            </p>
                        </div>
                    </article>
                </section>

                {/* === 8. INCOME CHART === */}
                <section
                    className="lp-section lp-section--alt"
                    data-testid="section-income-chart"
                >
                    <Eyebrow>the real numbers</Eyebrow>
                    <h2 className="lp-h2">
                        i'd rather you see the real numbers than a
                        screenshot.
                    </h2>
                    <p>
                        here's what market partners at every rank earned in
                        an average month, straight from monat's 2025 income
                        disclosure statement.
                    </p>

                    <div
                        className="lp-income-table-wrap"
                        data-testid="income-table-wrap"
                    >
                        <table
                            className="lp-income-table"
                            data-testid="income-table"
                        >
                            <thead>
                                <tr>
                                    <th scope="col">rank</th>
                                    <th scope="col">% of u.s. partners</th>
                                    <th scope="col">avg. monthly</th>
                                </tr>
                            </thead>
                            <tbody>
                                {INCOME_ROWS.map((row) => (
                                    <tr key={row[0]}>
                                        <th scope="row">{row[0]}</th>
                                        <td>{row[1]}</td>
                                        <td>{row[2]}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <p>
                        across all u.s. market partners, active &amp;
                        inactive, average earnings were $665 for the year,
                        and 44% earned no commissions. a lot of people
                        sign up just for the discount &amp; stop there
                        (totally fine btw), so most partners sit at the
                        first rank. where you go from there depends on
                        what you build.
                    </p>

                    <p className="lp-fineprint">
                        these figures are gross and don't include business
                        expenses. they are not guarantees or projections of
                        your earnings, and you can lose money. read the
                        full statement:{" "}
                        <a
                            href={IDS_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="income-ids-link"
                        >
                            monat income disclosure statement
                        </a>
                        .
                    </p>
                </section>

                {/* === 9. HOW TO START === */}
                <section
                    className="lp-section"
                    data-testid="section-how-to-start"
                >
                    <Eyebrow>how to start</Eyebrow>
                    <h2 className="lp-h2">
                        start with me, or try the products first.
                    </h2>
                    <p>
                        most practitioners will invest $10k to $100k in
                        certifications or equipment, or $3k to $15k in
                        courses, and still have no recurring revenue.
                    </p>

                    <div className="lp-start-grid">
                        <article
                            className="lp-start-card"
                            data-testid="start-with-me"
                        >
                            <h3 className="lp-start-title">start with me</h3>
                            <p className="lp-start-price">around $300</p>
                            <p>
                                that's your products to actually use on
                                yourself, your own storefront, our team
                                chat &amp; direct coaching with me. there's
                                a 30-day money-back guarantee.
                            </p>
                            <button
                                type="button"
                                className="lp-btn lp-btn--lime"
                                onClick={goBook}
                                data-testid="start-cta-book"
                            >
                                book a 30-minute call
                            </button>
                        </article>

                        <article
                            className="lp-start-card"
                            data-testid="try-products"
                        >
                            <h3 className="lp-start-title">
                                try the products
                            </h3>
                            <p className="lp-start-price">
                                around $110 to $150
                            </p>
                            <p>
                                you don't have to decide anything about the
                                business today. plenty of people want to
                                try the products first, &amp; that's a
                                great place to start. your starting cart
                                comes with a 30-day money-back guarantee.
                            </p>
                            <button
                                type="button"
                                className="lp-btn lp-btn--outline"
                                onClick={goTryProductsStart}
                                data-testid="start-cta-try"
                            >
                                find your hair routine →
                            </button>
                        </article>
                    </div>
                </section>

                {/* === 10. QUESTIONS === */}
                <section
                    className="lp-section lp-section--alt"
                    data-testid="section-questions"
                >
                    <Eyebrow>who it's for</Eyebrow>
                    <h2 className="lp-h2">
                        for practitioners whose patients already trust
                        their recommendations.
                    </h2>

                    <div className="lp-faq" data-testid="faq">
                        <FaqItem
                            testId="faq-mlm"
                            defaultOpen
                            q="is this an mlm?"
                            a={
                                <>
                                    it's direct sales, with monat, an
                                    established, debt-free haircare company
                                    of 12 years and counting. you can start
                                    by simply sharing your link with
                                    patients, and decide later whether you
                                    want to build a team. done one before?
                                    so have a lot of the women on my team.
                                    there are no quotas here and no monthly
                                    spend requirements.
                                </>
                            }
                        />
                        <FaqItem
                            testId="faq-ship"
                            q="do i have to stock or ship any inventory or retail products?"
                            a={
                                <>
                                    no. your patient orders through your
                                    link &amp; monat ships to her. you
                                    never handle inventory or payments.
                                </>
                            }
                        />
                        <FaqItem
                            testId="faq-call"
                            q="what happens on the call?"
                            a={
                                <>
                                    you tell me where you are in the
                                    decision. maybe you've already decided
                                    &amp; just want to know how to start.
                                    maybe you're curious &amp; have a list
                                    of questions. maybe you're pretty sure
                                    it's not for you &amp; want to rule it
                                    out. all of those are good reasons to
                                    book. i'll answer what you ask &amp;
                                    tell you whether i think it fits your
                                    practice.
                                </>
                            }
                        />
                    </div>
                </section>

                {/* === 11. FINAL CALL (ink card) === */}
                <section
                    className="lp-final-card"
                    data-testid="section-final"
                    id="book"
                >
                    <h2 className="lp-final-heading">
                        wherever you are in the decision, let's talk.
                    </h2>
                    <p>
                        it's free &amp; it's 30 minutes. bring your
                        questions about your practice, your patients &amp;
                        the numbers. if it's not a fit, i'll tell you.
                    </p>
                    <button
                        type="button"
                        className="lp-btn lp-btn--lime"
                        onClick={goBook}
                        data-testid="final-cta-book-call"
                    >
                        book a 30-minute call
                    </button>
                    <a
                        className="lp-final-small-link"
                        href={TRY_PRODUCTS_URL_FINAL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="final-try-link"
                    >
                        not ready? find your hair routine →
                    </a>
                </section>

                {/* === 12. FOOTER === */}
                <footer
                    className="lp-footer"
                    data-testid="disclosure-footer"
                >
                    <p>
                        kristen hinman is an independent monat market
                        partner. this page does not guarantee income.
                        results vary and depend on individual effort, time
                        and skill. see monat's{" "}
                        <a
                            href={IDS_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="footer-ids-link"
                        >
                            income disclosure statement
                        </a>
                        . monat products are cosmetics and are not intended
                        to diagnose, treat, cure or prevent any disease.
                    </p>
                </footer>
            </main>

            {/* Sticky mobile CTA */}
            <div
                className={`lp-sticky-cta ${stickyVisible ? "visible" : ""}`}
                data-testid="sticky-cta-wrapper"
            >
                <button
                    type="button"
                    className="lp-btn lp-btn--lime"
                    onClick={goBook}
                    data-testid="sticky-cta-book-call"
                >
                    book a 30-minute call
                </button>
            </div>
        </div>
    );
};

function App() {
    return (
        <BrowserRouter>
            <Routes>
                <Route path="/" element={<Landing />} />
            </Routes>
        </BrowserRouter>
    );
}

export default App;
